/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, Validators } from '@angular/forms';
import { L7RealServiceModel, L7VirtualServiceModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { Layer7ApiService } from '../../../../http';
import { LbErrorService, WizardControllerStateService, WizardService, WizardTemplateService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { ipValidator, nameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-layer7-real-server',
    templateUrl: './layer7-real.component.html',
    styleUrls: ['./layer7-real.component.scss']
})
export class Layer7RealComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Are we part of creating a service or just adding or editing a real server.
     */
    public isAddEdit: boolean = true;

    /**
     * Edit id set by parent component
     */
    public editId: string;

    /**
     * List of real servers.
     * @private
     */
    private realServers: L7RealServiceModel[] = [];

    /**
     * To store the callback for the wizard once the add is complete.
     * @private
     */
    private callBack: (...args: any[]) => void;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private layer7ApiService: Layer7ApiService,
        private wizardControllerStateService: WizardControllerStateService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
        this.componentTitle = WizardTitleEnum.REALSERVERS;
    }

    ngOnInit(): void {
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.initLoading();
    }

    /**
     * Gets the first frontend mode from the frontendModes @Input.
     * @private
     */
    get frontendPorts(): string {
        return this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.FRONTENDS}.${this.templateIndex}.ports.value`
        );
    }

    /**
     * Navigate back to the correct page.
     */
    public returnToParentPage(row?: any): void {
        if (row) {
            this.updateControllerStates(row);
            this.callBack(row);
        } else {
            const formRow: any = this.serviceForm.get(this.subStepEnum.GENERAL).value;
            this.deleteRealServer(formRow);
            this.callBack();
        }
    }

    /**
     * Delete ripId from backend.
     * @param row
     * @private
     */
    private deleteRealServer(row: any): void {
        const backends: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}`);
        const backend: any = backends[this.templateIndex];

        if (backend?.id?.value) {
            this.wizardControllerStateService.deleteServiceItemInControllerStateById(
                this.wizardService.controllerState, WizardTitleEnum.BACKENDS, backend.id.value, 'ripIds', row.id
            );
        }

        this.wizardControllerStateService.deleteServiceFromControllerStateByServiceId(
            this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, this.editId
        );
    }

    /**
     * Loads both the Health checks and fallback pages and set the values.
     * @private
     */
    private initLoading(): void {
        this.layer7ApiService.getRealServers()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (realServers: L7RealServiceModel[]): void => {
                    const serviceError: any = this.getErrorForService(WizardTitleEnum.REALSERVERS);
                    const template: any = this.getTemplate(WizardTitleEnum.REALSERVERS, serviceError, this.editId);

                    // Don't allow 'add another' if editing.
                    if (this.utilsService.isServiceId(template.id?.value)) {
                        this.wizardService.excludeFromAddAnother = WizardTitleEnum.REALSERVERS;
                    }

                    const services: L7VirtualServiceModel[] = this.formatServices(realServers, WizardTitleEnum.REALSERVERS);
                    this.realServers = this.filterServicesByName(template, services);

                    this.patchTemplateToForm(template);
                    // Once everything is loaded check to see if there are any error in the controller state
                    if (serviceError?.error?.length) {
                        this.loadedWithErrors(serviceError.error);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServicesRealComponent:initLoading', error.message);
                }
            });
    }

    /**
     * Initialises the form build.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Populate our patch
        const templatePatch: any = this.createTemplatePatch(template);

        // Format name and prefix
        const newLabel: any = this.wizardService.selectedTemplate.wizardDefaultMode === 'partner'
            ? this.getFrontendName() : this.utilsService.getObjectValue(template, 'name.value');
        this.formatLabel(templatePatch, newLabel);

        this.setupFrontendPortContent();

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.REALSERVERS);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        this.initialFormData = templatePatch;

        // Patching can change the view, so we should kick the change detector.
        this.cdr.detectChanges();

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;
    }

    /**
     * Set up content based on frontend port.
     * @private
     */
    private setupFrontendPortContent(): void {
        if (this.frontendPorts?.length > 1) {
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.port').disable();
        } else {
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.port').enable();
        }
    }

    /**
     * Set up the form group
     * @private
     */
    private setupFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                id: ['temp-' + Date.now() + Math.random()], // <- Used outside of this component.
                name: ['', [nameValidation, Validators.required]],
                ip: ['', [ipValidator, Validators.required]],
                enabledCount: [false],
                count: [1, Validators.required],
                port: [null, portRangeValidation],
                encryptToBackend: [false],
                weight: [100, Validators.required],
                disabled: [false],
                minconns: [0],
                maxconns: [0]
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.REALSERVERS);
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL).valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('realservers', (): void => {
                        if (this.serviceForm.valid && this.isAddEdit) {
                            this.updateControllerStates(values);
                            this.wizardService.isSectionValidEmitter.next(this.serviceForm.valid);
                        }

                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, values)) {
                            this.serviceForm.markAsPristine();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: string): void => {
                    this.checkNameForDuplicates(name, this.realServers);
                }
            });
    }

    /**
     * Update the controller state
     * @param newRealServer
     * @private
     */
    private updateControllerStates(newRealServer: any): void {
        const realServers: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.REALSERVERS}`);
        const realServerIndex: any = realServers
            ? realServers.findIndex((item: any): boolean => item.id.value === newRealServer.id)
            : -1;

        const template: any = this.outputFormValues(newRealServer);

        if (realServers && realServerIndex !== -1) {
            this.wizardControllerStateService.updateServiceInControllerStateById(
                this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, newRealServer.id, template
            );
        } else {
            this.wizardControllerStateService.addServiceToControllerState(
                this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, template
            );
        }

        // Only update the backend if we are adding a new real server, and we are not adding/editing a backend.
        if (this.isAddEdit && !this.utilsService.isServiceId(newRealServer.id)) {
            this.updateBackendControllerState(newRealServer);
        }
    }

    /**
     * Format output values.
     * @param newRealServer
     * @param template
     * @private
     */
    private outputFormValues(newRealServer: any, template?: undefined): any {
        const newTemplate: object = template ? template : {};
        for (const property in newRealServer) {
            if (newRealServer.hasOwnProperty(property)) {
                const visibility: any = this.inputStateStore[property] && this.inputStateStore[property].hidden;
                newTemplate[property] = {
                    value: newRealServer[property],
                    hidden: visibility !== undefined ? visibility : true
                };
            }
        }

        return newTemplate;
    }

    /**
     * Update backend controller state.
     * @param newRealServer
     * @private
     */
    private updateBackendControllerState(newRealServer: any): void {
        const backends: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}`);
        if (!backends) {
            this.logService.error('updateBackendControllerState', 'No backends found.');

            return;
        }
        const backend: any = backends[backends.length > 1 ? this.templateIndex : 0];
        this.wizardControllerStateService.updateServiceItemInControllerStateById(
            this.wizardService.controllerState,
            WizardTitleEnum.BACKENDS,
            backend.id.value,
            'ripIds',
            [newRealServer.id]
        );
    }
}
